// A SESSÃO tem fim, e até 02/10/2026 ela não tinha.
//
// `getOrCreateLocalId("synqi3_session_id")` guardava o id em `localStorage` e **nunca o trocava**. Então
// a "sessão" de um visitante durava meses: a mesma pessoa voltando em janeiro e em março era uma sessão
// só. E o KPI `sessions` do catálogo é `agg: distinct_sessions` — ou seja, ele contava PESSOAS com nome
// de sessão, e toda taxa por sessão (conversão por sessão, páginas por sessão, permanência por sessão)
// media outra coisa.
//
// Medido nos três dias anteriores, nos eventos que vieram do SDK de navegador: `agorafecha` com 10
// eventos, 10 sessões e 10 anônimos — razão de 1,00 sessão por pessoa, que é o que se espera quando a
// sessão nunca termina. A amostra é pequena e não conclui sozinha; o defeito está no código e esse se lê.
//
// ## A regra
//
// Trinta minutos de inatividade fecham a sessão, e a virada do dia também. Os trinta minutos são a
// convenção da indústria (GA, Adobe, Matomo usam o mesmo), e importam aqui porque é o número que deixa
// a nossa medição comparável com a que o inquilino já tem em outra ferramenta. A virada do dia existe
// porque série diária com sessão atravessando a meia-noite atribui metade do comportamento ao dia errado.
//
// ## O que isto MUDA nos números de quem já usa o SDK
//
// O `sessions` vai SUBIR, e `conversões por sessão` vai CAIR. Nenhum dos dois é regressão: o número de
// antes estava errado. Mas é mudança visível, então quem opera precisa saber a data do corte — por isso
// o `session_start` carrega `reason`, e a série fica explicável.

export const INATIVIDADE_MS = 30 * 60 * 1000;

export const CHAVE_ID = "synqi3_session_id";
export const CHAVE_VISTO = "synqi3_session_visto";     // último sinal de vida, em ms
export const CHAVE_DIA = "synqi3_session_dia";         // o dia local em que ela começou
export const CHAVE_PRIMEIRA = "synqi3_first_visit";    // a PRIMEIRA visita de todas, em ISO

/**
 * O id da sessão de agora, rodando-a quando ela expirou.
 *
 * @param {object} o
 * @param {{getItem:Function,setItem:Function}} o.storage
 * @param {()=>string} o.novoId
 * @param {()=>number} [o.now]
 * @param {()=>string} [o.dia]  o dia LOCAL, `YYYY-MM-DD`
 * @returns {{id:string, nova:boolean, motivo:("first"|"inactivity"|"day_rollover"|undefined)}}
 */
export function sessaoDeAgora({ storage, novoId, now = Date.now, dia = diaLocal }) {
  const agora = now();
  const hoje = dia();
  let id, visto, diaAnterior;
  try {
    id = storage.getItem(CHAVE_ID);
    visto = parseInt(storage.getItem(CHAVE_VISTO) || "0", 10);
    diaAnterior = storage.getItem(CHAVE_DIA);
  } catch {
    // Sem storage não há continuidade possível: cada chamada é uma sessão nova, e dizer isso é melhor
    // que inventar uma que não persiste.
    return { id: novoId(), nova: true, motivo: "first" };
  }

  let motivo;
  // Os VALORES estão em inglês, e a chave do fio é `reason`: eles são comparados numa condição de
  // regra e lidos num filtro de consulta, pelo analista do inquilino. O nome da variável segue em
  // português porque é código desta casa — a fronteira é o fio, não o arquivo. Medido em
  // 02/10/2026: os presets da plataforma já comparam `downgrade`, `vip`, `2_renewal`.
  if (!id) motivo = "first";
  else if (!visto || agora - visto > INATIVIDADE_MS) motivo = "inactivity";
  else if (diaAnterior && diaAnterior !== hoje) motivo = "day_rollover";

  if (motivo) id = novoId();

  try {
    storage.setItem(CHAVE_ID, id);
    storage.setItem(CHAVE_VISTO, String(agora));
    if (motivo) storage.setItem(CHAVE_DIA, hoje);
  } catch { /* storage cheio ou bloqueado: o id vale para esta página e não persiste */ }

  return { id, nova: Boolean(motivo), motivo };
}

/**
 * A PRIMEIRA visita desta pessoa, em ISO — criada na primeira vez e nunca reescrita.
 *
 * O nome e o formato vêm do que o lake JÁ tem: medido em 02/10/2026, os 802 `session_start` do tenant
 * `synqi3` carregam `first_visit` com um timestamp ISO (`2026-02-04T14:27:32.066Z`), não um booleano.
 * Parece detalhe e não é: booleano responde "é a primeira?" e morre; o timestamp responde isso E
 * "quanto tempo ela nos conhece", que é a pergunta que a personalização faz de verdade.
 *
 * Sem storage não há primeira visita possível de afirmar — e nesse caso devolvemos `undefined` em vez
 * de `agora`, porque dizer "a primeira visita foi agora" a cada carregamento transformaria todo
 * visitante em novato e é precisamente a afirmação que ninguém verificou.
 *
 * @returns {string|undefined}
 */
export function primeiraVisita({ storage, now = Date.now }) {
  if (!storage) return undefined;
  try {
    const guardado = storage.getItem(CHAVE_PRIMEIRA);
    if (guardado) return guardado;
    const iso = new Date(now()).toISOString();
    storage.setItem(CHAVE_PRIMEIRA, iso);
    return iso;
  } catch {
    return undefined;
  }
}

/** Só renova o sinal de vida. Chamado a cada evento, sem decidir nada. */
export function marcarVida({ storage, now = Date.now }) {
  try {
    storage.setItem(CHAVE_VISTO, String(now()));
  } catch { /* idem */ }
}

/** O dia LOCAL da pessoa, `YYYY-MM-DD`. Local e não UTC: a meia-noite que importa é a dela. */
export function diaLocal(d = new Date()) {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
